import React, { useState, useEffect } from "react";
import { FiShield, FiUser, FiStar, FiSearch } from "react-icons/fi";
import { postRequestAuthenticated } from "../../../services/rest";

// Fixed role privileges (not customizable for now — just assignment).
const PRIVILEGES = [
  {
    role: "Owner",
    icon: FiStar,
    desc: "Full control: channel, topics, members, roles & billing. Set at creation, can't be reassigned here.",
  },
  {
    role: "Admin",
    icon: FiShield,
    desc: "Manage topics, posts, events & polls, and members. Can't change the owner or billing.",
  },
  {
    role: "Member",
    icon: FiUser,
    desc: "Read & participate — post, react, join topics.",
  },
];

const uidOf = (m) => (m.user && m.user._id) || m.user;

const RolesPanel = () => {
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState("");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const PER = 10;

  const shownMembers = members.filter((m) => {
    if (!q.trim()) return true;
    const u = m.user || {};
    return `${u.name || ""} ${u.username || ""} ${u.email || ""}`
      .toLowerCase()
      .includes(q.trim().toLowerCase());
  });
  const totalPages = Math.max(1, Math.ceil(shownMembers.length / PER));
  const pageMembers = shownMembers.slice((page - 1) * PER, page * PER);

  useEffect(() => {
    setPage(1);
  }, [channelId, q]);

  // owned channels only — only the owner can assign roles
  useEffect(() => {
    postRequestAuthenticated("/fetch/my/channels", {})
      .then((res) => {
        if (res?.success) {
          const owned = (res.channels || []).filter((c) => c.isOwner);
          setChannels(owned);
          if (owned[0]) setChannelId(owned[0]._id);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!channelId) {
      setMembers([]);
      return;
    }
    setLoading(true);
    setError("");
    postRequestAuthenticated("/fetch/business/roles/members", { channelId })
      .then((res) => {
        if (res?.success) setMembers(res.members || []);
      })
      .finally(() => setLoading(false));
  }, [channelId]);

  const changeRole = (m, role) => {
    const uid = uidOf(m);
    if (!uid || m.role === role) return;
    setSavingId(uid);
    setError("");
    postRequestAuthenticated("/update/user/business/role", {
      userId: uid,
      role,
      channelId,
    })
      .then((res) => {
        if (res?.success) {
          setMembers((prev) =>
            prev.map((x) => (uidOf(x) === uid ? { ...x, role } : x))
          );
        } else {
          setError(res?.message || "Could not update role.");
        }
      })
      .catch(() => setError("Could not update role."))
      .finally(() => setSavingId(null));
  };

  return (
    <div className="mt-5 rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-5">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-theme-secondaryText">
          Roles &amp; permissions
        </h3>
        {channels.length > 0 && (
          <select
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
            className="rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-3 py-1.5 text-sm text-theme-secondaryText outline-none"
          >
            {channels.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>
        )}
      </div>
      <p className="mb-3 text-xs text-theme-placeholder">
        Assign roles for members of a channel. Only the owner can change roles.
      </p>

      {channels.length > 0 && members.length > 0 && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-3 py-1.5">
          <FiSearch size={14} className="text-theme-placeholder" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search members by name or email…"
            className="w-full bg-transparent text-sm text-theme-secondaryText outline-none placeholder:text-theme-placeholder"
          />
        </div>
      )}

      {/* privilege reference */}
      <div className="mb-5 grid grid-cols-1 gap-2 sm:grid-cols-3">
        {PRIVILEGES.map((p) => (
          <div
            key={p.role}
            className="rounded-xl border border-theme-chatDivider p-3"
          >
            <div className="flex items-center gap-2">
              <p.icon size={14} className="text-theme-accent" />
              <span className="text-sm font-medium text-theme-secondaryText">
                {p.role}
              </span>
            </div>
            <p className="mt-1 text-xs text-theme-placeholder">{p.desc}</p>
          </div>
        ))}
      </div>

      {error && (
        <p className="mb-3 text-sm text-theme-error">{error}</p>
      )}

      {channels.length === 0 ? (
        <p className="py-6 text-center text-sm text-theme-placeholder">
          You don't own any channels yet.
        </p>
      ) : loading ? (
        <p className="py-6 text-center text-sm text-theme-placeholder">
          Loading members…
        </p>
      ) : members.length === 0 ? (
        <p className="py-6 text-center text-sm text-theme-placeholder">
          No members in this channel yet (besides you).
        </p>
      ) : shownMembers.length === 0 ? (
        <p className="py-6 text-center text-sm text-theme-placeholder">
          No members match "{q}".
        </p>
      ) : (
        <div className="flex flex-col divide-y divide-theme-chatDivider">
          {pageMembers.map((m) => {
            const uid = uidOf(m);
            const u = m.user || {};
            const name = u.name || u.username || "Member";
            return (
              <div
                key={uid}
                className="flex items-center justify-between gap-3 py-3"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span
                    className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-gray-900"
                    style={{ background: u.color_logo || "#C4C4C4" }}
                  >
                    {name.charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-theme-secondaryText">
                      {name}
                    </p>
                    <p className="truncate text-xs text-theme-placeholder">
                      {u.email || "—"}
                    </p>
                  </div>
                </div>
                <select
                  value={m.role === "owner" ? "owner" : m.role || "member"}
                  disabled={savingId === uid || m.role === "owner"}
                  onChange={(e) => changeRole(m, e.target.value)}
                  className="flex-shrink-0 rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-3 py-1.5 text-sm text-theme-secondaryText outline-none disabled:opacity-50"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                  {m.role === "owner" && <option value="owner">Owner</option>}
                </select>
              </div>
            );
          })}
        </div>
      )}

      {shownMembers.length > PER && (
        <div className="mt-4 flex items-center justify-between">
          <span className="text-xs text-theme-placeholder">
            Page {page} of {totalPages} · {shownMembers.length} members
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-lg border border-theme-chatDivider px-3 py-1.5 text-xs font-semibold text-theme-primaryText transition-colors hover:bg-theme-tertiaryBackground disabled:opacity-40"
            >
              Prev
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="rounded-lg border border-theme-chatDivider px-3 py-1.5 text-xs font-semibold text-theme-primaryText transition-colors hover:bg-theme-tertiaryBackground disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesPanel;
